/**
 * Browser helpers for marketing capture (`d_customer_session`) and lightweight UI analytics.
 *
 * - buildCampaignCapturePayload() — same field names as backend `_CAMPAIGN_FIELDS` / CampaignSubmitIn.
 *   Any of those may be set on the **URL query string** (e.g. `?utm_source=google&ingestion_source=PAID_META`);
 *   URL values override auto-detected browser values when present. Code `overrides` argument wins last.
 * - submitCustomerSessionCapture({ mobile, entity_type }) — optional standalone POST /api/v1/event-logs.
 * - trackEvent() — client-side funnel breadcrumbs only (does not call event-logs; see notes in trackEvent).
 */

/** Mirrors `app/campaign/campaign.py` `_CAMPAIGN_FIELDS` — use as query params on ad landing URLs. */
export const CAMPAIGN_QUERY_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'capture_page_path',
  'capture_page_url',
  'capture_page_query',
  'capture_referrer_url',
  'platform',
  'device_type',
  'device_model',
  'os_name',
  'os_version',
  'browser_name',
  'browser_version',
  'app_version',
  'environment',
  'release_tag',
  'user_agent',
  'viewport_width',
  'viewport_height',
  'screen_width',
  'screen_height',
  'capture_language',
  'timezone_offset_min',
  'lead_source',
  'ingestion_source',
];

/** Max string lengths aligned with `insert_d_customer_session_capture` / CampaignSubmitIn. */
const CAMPAIGN_QUERY_MAX_LEN = {
  utm_source: 120,
  utm_medium: 120,
  utm_campaign: 200,
  utm_content: 200,
  capture_page_path: 1024,
  capture_page_url: 8000,
  capture_page_query: 8000,
  capture_referrer_url: 8000,
  platform: 20,
  device_type: 20,
  device_model: 200,
  os_name: 64,
  os_version: 32,
  browser_name: 64,
  browser_version: 32,
  app_version: 64,
  environment: 32,
  release_tag: 64,
  user_agent: 8000,
  capture_language: 32,
  lead_source: 120,
  ingestion_source: 40,
};

const CAMPAIGN_QUERY_INT_FIELDS = new Set([
  'viewport_width',
  'viewport_height',
  'screen_width',
  'screen_height',
  'timezone_offset_min',
]);
const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || process.env.VITE_API_BASE_URL || process.env.VITE_API_URL || '';
const PUBLIC_API_KEY = process.env.REACT_APP_PUBLIC_API_KEY || process.env.VITE_PUBLIC_API_KEY || '';

const ANON_KEY = 'solvetax_anon_customer_id';
const SESSION_KEY = 'solvetax_session_id';
const JOURNEY_KEY_PREFIX = 'solvetax_journey_id:';
const RELEASE_TAG = process.env.REACT_APP_RELEASE_TAG || process.env.VITE_RELEASE_TAG || null;
const APP_VERSION = process.env.REACT_APP_APP_VERSION || process.env.VITE_APP_VERSION || RELEASE_TAG;

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getOrCreate = (storage, key, factory) => {
  try {
    const existing = storage.getItem(key);
    if (existing) return existing;
    const value = factory();
    storage.setItem(key, value);
    return value;
  } catch {
    return factory();
  }
};

const getAnonymousCustomerId = () => getOrCreate(localStorage, ANON_KEY, () => uuid().replace(/-/g, '').slice(0, 32));
const getSessionId = () => getOrCreate(sessionStorage, SESSION_KEY, uuid);

const parseBrowser = (uaRaw) => {
  const ua = uaRaw || '';
  const rules = [
    { name: 'EDGE', regex: /Edg\/([\d.]+)/i },
    { name: 'CHROME', regex: /Chrome\/([\d.]+)/i },
    { name: 'FIREFOX', regex: /Firefox\/([\d.]+)/i },
    { name: 'SAFARI', regex: /Version\/([\d.]+).*Safari/i },
  ];
  for (const item of rules) {
    const match = ua.match(item.regex);
    if (match) return { browser_name: item.name, browser_version: match[1] || null };
  }
  return { browser_name: null, browser_version: null };
};

const parseOs = (uaRaw) => {
  const ua = uaRaw || '';
  const rules = [
    { name: 'WINDOWS', regex: /Windows NT ([\d.]+)/i },
    { name: 'ANDROID', regex: /Android ([\d.]+)/i },
    { name: 'IOS', regex: /OS ([\d_]+) like Mac OS X/i, normalize: (v) => v.replace(/_/g, '.') },
    { name: 'MAC', regex: /Mac OS X ([\d_]+)/i, normalize: (v) => v.replace(/_/g, '.') },
    { name: 'LINUX', regex: /Linux/i, version: null },
  ];
  for (const item of rules) {
    const match = ua.match(item.regex);
    if (match) {
      const version = item.version !== undefined ? item.version : (item.normalize ? item.normalize(match[1] || '') : (match[1] || null));
      return { os_name: item.name, os_version: version };
    }
  }
  return { os_name: null, os_version: null };
};

const detectDeviceType = () => {
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  if (width > 0 && width < 768) return 'PHONE';
  if (width >= 768 && width < 1024) return 'TABLET';
  return 'DESKTOP';
};

const detectPlatform = (uaRaw) => {
  const ua = uaRaw || '';
  if (/Android/i.test(ua)) return 'ANDROID';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'IOS';
  return 'WEB';
};

const parseReferrerPath = () => {
  try {
    if (!document.referrer) return null;
    return new URL(document.referrer).pathname || null;
  } catch {
    return document.referrer || null;
  }
};

/** Full referrer URL for capture_referrer_url (matches backend CampaignSubmitIn). */
const captureReferrerUrl = () => {
  try {
    return document.referrer || null;
  } catch {
    return null;
  }
};

/**
 * Read campaign capture fields from the query string (ad final URLs, deep links).
 * Integer params must be base-10; invalid numbers are skipped.
 */
const parseCampaignQueryParams = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    return {};
  }
  const out = {};
  for (const key of CAMPAIGN_QUERY_FIELDS) {
    const raw = params.get(key);
    if (raw == null) continue;
    const s = String(raw).trim();
    if (!s) continue;
    if (CAMPAIGN_QUERY_INT_FIELDS.has(key)) {
      const n = parseInt(s, 10);
      if (!Number.isNaN(n)) out[key] = n;
      continue;
    }
    const max = CAMPAIGN_QUERY_MAX_LEN[key] ?? 8000;
    out[key] = s.slice(0, max);
  }
  return out;
};

const deriveLeadSourceFromUtms = (utm) => {
  const raw = utm.utm_campaign || utm.utm_source || utm.utm_medium;
  if (!raw) return null;
  const s = String(raw).trim();
  return s ? s.slice(0, 120) : null;
};

/**
 * Snapshot of browser / page context for `d_customer_session` (same names as backend capture fields).
 * Merge into public customer or income-tax JSON bodies, or pass to submitCustomerSessionCapture.
 * @param {Record<string, unknown>} overrides — e.g. lead_source: 'WEBSITE' to force CRM/session lead_source
 *
 * URL query: any key in CAMPAIGN_QUERY_FIELDS may be set on the landing URL; those override
 * detected values. If `lead_source` is absent, it is still derived from utm_campaign → utm_source → utm_medium.
 */
export function buildCampaignCapturePayload(overrides = {}) {
  if (typeof window === 'undefined') {
    return { ...overrides };
  }
  const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
  const { browser_name, browser_version } = parseBrowser(userAgent);
  const { os_name, os_version } = parseOs(userAgent);
  const loc = window.location;
  const fromQuery = parseCampaignQueryParams(loc.search);
  const utm = {
    utm_source: fromQuery.utm_source,
    utm_medium: fromQuery.utm_medium,
    utm_campaign: fromQuery.utm_campaign,
    utm_content: fromQuery.utm_content,
  };
  let device_model = null;
  try {
    const uad = navigator.userAgentData;
    if (uad && typeof uad.mobile === 'boolean') {
      const plat = typeof uad.platform === 'string' ? uad.platform : '';
      device_model = [plat, uad.mobile ? 'MOBILE' : 'DESKTOP'].filter(Boolean).join(' ').slice(0, 200) || null;
    }
  } catch {
    device_model = null;
  }
  let timezone_offset_min = null;
  try {
    timezone_offset_min = new Date().getTimezoneOffset();
    if (typeof timezone_offset_min !== 'number' || Number.isNaN(timezone_offset_min)) timezone_offset_min = null;
  } catch {
    timezone_offset_min = null;
  }
  let capture_language = null;
  try {
    const lang = navigator.language || navigator.userLanguage;
    if (lang) capture_language = String(lang).slice(0, 32);
  } catch {
    capture_language = null;
  }
  const derivedLead = deriveLeadSourceFromUtms(utm);
  const browserPayload = {
    capture_page_path: loc.pathname ? String(loc.pathname).slice(0, 1024) : null,
    capture_page_url: loc.href || null,
    capture_page_query: loc.search ? String(loc.search).replace(/^\?/, '').slice(0, 8000) : null,
    capture_referrer_url: captureReferrerUrl(),
    platform: detectPlatform(userAgent),
    device_type: detectDeviceType(),
    device_model,
    os_name,
    os_version,
    browser_name,
    browser_version,
    app_version: APP_VERSION || null,
    environment: typeof process !== 'undefined' && process.env && process.env.NODE_ENV ? process.env.NODE_ENV : 'development',
    release_tag: RELEASE_TAG,
    user_agent: userAgent || null,
    viewport_width: typeof window.innerWidth === 'number' ? window.innerWidth : null,
    viewport_height: typeof window.innerHeight === 'number' ? window.innerHeight : null,
    screen_width: typeof screen !== 'undefined' && typeof screen.width === 'number' ? screen.width : null,
    screen_height: typeof screen !== 'undefined' && typeof screen.height === 'number' ? screen.height : null,
    capture_language,
    timezone_offset_min,
    ingestion_source: 'WEB_APP',
  };
  const payload = {
    ...browserPayload,
    ...fromQuery,
    ...overrides,
  };
  if (overrides.lead_source === undefined) {
    if (!payload.lead_source && derivedLead) {
      payload.lead_source = derivedLead;
    }
  }
  return payload;
}

/**
 * Standalone insert into d_customer_session via POST /api/v1/event-logs (CampaignSubmitIn).
 * Requires PUBLIC_API_KEY header when backend is configured.
 * @returns {Promise<{ ok?: boolean, customer_session_id?: number }|null>}
 */
export async function submitCustomerSessionCapture({
  mobile,
  entity_type = 'CUSTOMER',
  transport,
  ...captureOverrides
} = {}) {
  if (!API_BASE) return null;
  const digits = String(mobile || '').replace(/\D/g, '');
  const mob = digits.length >= 10 ? digits.slice(-10) : '';
  if (mob.length !== 10) return null;
  const body = {
    mobile: mob,
    entity_type: entity_type || 'UNKNOWN',
    ...buildCampaignCapturePayload(captureOverrides),
  };
  const url = `${API_BASE}/api/v1/event-logs`;
  const headers = {
    'Content-Type': 'application/json',
    ...(PUBLIC_API_KEY ? { 'X-Public-Api-Key': PUBLIC_API_KEY } : {}),
  };
  try {
    if (transport === 'beacon' && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return { ok: true };
    }
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export const getJourneyId = (funnelName, { reset = false } = {}) => {
  if (!funnelName) return null;
  const key = `${JOURNEY_KEY_PREFIX}${funnelName}`;
  if (reset) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore storage failures
    }
  }
  return getOrCreate(sessionStorage, key, uuid);
};

/**
 * Funnel / UX breadcrumbs for debugging or future analytics pipeline.
 * Does not POST to /api/v1/event-logs (that endpoint expects CampaignSubmitIn + mobile).
 */
export const trackEvent = async (event) => {
  const nowIso = new Date().toISOString();
  const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : null;
  const { browser_name, browser_version } = parseBrowser(userAgent);
  const { os_name, os_version } = parseOs(userAgent);
  const payload = {
    session_id: getSessionId(),
    anonymous_customer_id: getAnonymousCustomerId(),
    client_event_id: event.client_event_id || uuid(),
    event_name: event.event_name,
    event_category: event.event_category || 'ui',
    event_action: event.event_action || null,
    event_status: event.event_status || null,
    event_label: event.event_label || null,
    severity: event.severity || 'info',
    page_path: event.page_path || window.location.pathname,
    page_url: event.page_url || window.location.href,
    referrer_path: event.referrer_path || parseReferrerPath(),
    route_name: event.route_name || null,
    cta_name: event.cta_name || null,
    form_name: event.form_name || null,
    funnel_name: event.funnel_name || null,
    funnel_step_number: event.funnel_step_number ?? null,
    funnel_step_name: event.funnel_step_name || null,
    journey_id: event.journey_id || (event.funnel_name ? getJourneyId(event.funnel_name) : null),
    service_code: event.service_code || null,
    phone_number: event.phone_number || null,
    event_timestamp: event.event_timestamp || nowIso,
    dwell_time_seconds: event.dwell_time_seconds ?? null,
    active_time_seconds: event.active_time_seconds ?? null,
    api_name: event.api_name || null,
    api_status_code: event.api_status_code ?? null,
    api_response_time_ms: event.api_response_time_ms ?? null,
    error_code: event.error_code || null,
    error_message: event.error_message || null,
    ingestion_source: 'web_app',
    environment: process.env.NODE_ENV || 'development',
    release_tag: RELEASE_TAG,
    platform: event.platform || detectPlatform(userAgent),
    device_type: event.device_type || detectDeviceType(),
    os_name: event.os_name || os_name,
    os_version: event.os_version || os_version,
    browser_name: event.browser_name || browser_name,
    browser_version: event.browser_version || browser_version,
    app_version: event.app_version || APP_VERSION,
    user_agent: userAgent,
  };
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[SolveTax trackEvent]', payload);
  }
};

export const trackPageLifecycle = ({ funnel_name, form_name, service_code, open_event_name }) => {
  const startedAt = Date.now();
  const journeyId = funnel_name ? getJourneyId(funnel_name) : null;
  let leaveTracked = false;
  trackEvent({
    event_name: 'page_view',
    event_action: 'open',
    event_status: 'success',
    form_name,
    funnel_name,
    service_code,
    journey_id: journeyId,
  });
  if (open_event_name) {
    trackEvent({
      event_name: open_event_name,
      event_action: 'open',
      event_status: 'success',
      form_name,
      funnel_name,
      service_code,
      journey_id: journeyId,
      funnel_step_number: 1,
      funnel_step_name: 'form_open',
    });
  }

  const emitLeave = () => {
    if (leaveTracked) return;
    leaveTracked = true;
    const dwellSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
    trackEvent({
      event_name: 'page_leave',
      event_action: 'leave',
      event_status: 'success',
      form_name,
      funnel_name,
      service_code,
      journey_id: journeyId,
      dwell_time_seconds: dwellSeconds,
      active_time_seconds: dwellSeconds,
      transport: 'beacon',
    });
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      emitLeave();
    }
  };

  window.addEventListener('pagehide', emitLeave);
  window.addEventListener('beforeunload', emitLeave);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    window.removeEventListener('pagehide', emitLeave);
    window.removeEventListener('beforeunload', emitLeave);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    emitLeave();
  };
};
